import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { track, identifyUser } from '../../lib/mixpanel';
import { sendLeadToCapi } from '../../lib/metaCapi';
import CircularProgress from '@mui/material/CircularProgress';
import { ReactComponent as Logo } from '../../svg/newlogo.svg';
import { ReactComponent as PhoneSvg } from '../../svg/phonesvg.svg';
import { ReactComponent as SpeechBubble } from '../../svg/speech11bubble.svg';
import styles from '../../styles/sunbaebriefingcentum2mobile.module.css';
import tabStyles from '../../styles/sunbaebriefingcenterTabs.module.css';
import {
  useCentumBriefingStats,
  formatDday,
  formatRelativeTime,
} from '../../lib/useCentumBriefingStats';
import {
  BriefingBranchConfig,
  BRIEFING_BRANCHES,
  DEFAULT_CENTER_BRANCH_INDEX,
  ALL_BRIEFING_CLOSED,
} from '../../lib/briefingBranchConfig';

const ChevronDown = () => (
  <svg className={styles.fieldChevron} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 7.5L10 12.5L15 7.5" stroke="#333d4b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function readInitialBranchIndex(): number {
  if (typeof window === 'undefined') return DEFAULT_CENTER_BRANCH_INDEX;
  const raw = (window.location.hash || '').replace('#', '');
  if (!raw) return DEFAULT_CENTER_BRANCH_INDEX;
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    decoded = raw;
  }
  const idx = BRIEFING_BRANCHES.findIndex(b => b.introduceLocationKey === decoded);
  return idx >= 0 ? idx : DEFAULT_CENTER_BRANCH_INDEX;
}

interface SelectOption { value: string; label: string; }
interface CustomSelectProps {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  options: SelectOption[];
  disabled?: boolean;
}

const CustomSelect: React.FC<CustomSelectProps> = ({ value, onChange, placeholder, options, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedLabel = options.find(o => o.value === value)?.label;

  return (
    <div ref={ref} className={styles.customSelect}>
      <button
        type="button"
        className={styles.customSelectBtn}
        disabled={disabled}
        onClick={() => setOpen(prev => !prev)}
      >
        <span className={!value ? styles.selectPlaceholder : styles.selectValue}>
          {selectedLabel || placeholder}
        </span>
        <ChevronDown />
      </button>
      {open && (
        <ul className={styles.customSelectList}>
          {options.map(opt => (
            <li
              key={opt.value}
              className={`${styles.customSelectItem} ${opt.value === value ? styles.customSelectItemActive : ''}`}
              onMouseDown={() => { onChange(opt.value); setOpen(false); }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

function useCountUp(target: number, duration: number, start: boolean) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return val;
}

function MobileSocialProofBand({ branch }: { branch: BriefingBranchConfig }) {
  const countRef = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    if (!countRef.current) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) { setVis(true); io.disconnect(); }
      },
      { threshold: 0.9 }
    );
    io.observe(countRef.current);
    return () => io.disconnect();
  }, []);

  const stats = useCentumBriefingStats(branch.location);
  const totalWithOffset = (stats.total ?? 0) + branch.totalOffset;
  const applicants = useCountUp(totalWithOffset, 1600, vis && stats.total !== null);

  const tickerLen = stats.recentApplicants.length;
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (tickerLen === 0) return;
    setIdx(i => i % tickerLen);
    const id = setInterval(() => setIdx(i => (i + 1) % tickerLen), 2600);
    return () => clearInterval(id);
  }, [tickerLen]);

  void stats.tick;
  const dday = formatDday(branch.eventISO);
  const now = new Date();

  return (
    <section className={styles.proof}>
      <div className={styles.proofBg} style={{ backgroundImage: "url('/img/briefing/crowd-hall.jpeg')" }} />
      <div className={styles.proofOverlay} />
      <div className={styles.proofInner}>
        <div className={styles.proofTop}>
          <div className={styles.proofEyebrow}>
            <span className={styles.proofPulse} />
            LIVE · 실시간 신청 현황
          </div>
          <div className={styles.proofDday}>
            <b>{dday}</b> 마감
          </div>
        </div>

        <p className={styles.proofLabel}>지금까지 설명회를 신청한 학생·학부모</p>
        <div className={styles.proofCount} ref={countRef}>
          <span className={styles.proofNum}>
            {stats.total !== null && applicants > 0 ? applicants.toLocaleString() : '—'}
          </span>
          <span className={styles.proofUnit}>명</span>
        </div>
        <p className={styles.proofSub}>
          최근 24시간{' '}
          <span className={styles.pos}>
            {stats.last24h !== null ? `+${stats.last24h + branch.last24hOffset}명` : '—'}
          </span>{' '}
          신청
        </p>

        <hr className={styles.proofHair} />

        <div className={styles.proofTicker}>
          <span className={styles.proofTickerDot} />
          <div className={styles.proofTickerList}>
            {stats.recentApplicants.map((a, i) => (
              <div
                key={`${a.createdAt}-${i}`}
                className={`${styles.proofTickerItem} ${i === idx ? styles.isActive : ''}`}
              >
                <span><b>{a.name}</b>님 설명회 신청</span>
                <span className={styles.proofTickerTime}>{formatRelativeTime(a.createdAt, now)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function SunbaeBriefingCenterMobile() {
  const navigate = useNavigate();
  const isSoNarrow = useMediaQuery({ query: '(max-width: 385px)' });

  const [branchIdx, setBranchIdx] = useState<number>(() => readInitialBranchIndex());
  const branch = BRIEFING_BRANCHES[branchIdx];

  const [attendanceType, setAttendanceType] = useState('');
  const [gender, setGender] = useState('');
  const [name, setName] = useState('');
  const [phoneMiddle, setPhoneMiddle] = useState('');
  const [phoneLast, setPhoneLast] = useState('');
  const [enrollmentType, setEnrollmentType] = useState('');
  const [loading, setLoading] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const phoneLastRef = useRef<HTMLInputElement>(null);

  const formType = useMemo(() => `briefing_center_${branch.location}`, [branch.location]);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const handler = () => {
      setKeyboardOpen(vv.height < window.innerHeight - 250);
    };
    vv.addEventListener('resize', handler);
    return () => vv.removeEventListener('resize', handler);
  }, []);

  const isFormValid =
    attendanceType !== '' &&
    gender !== '' &&
    name.trim() !== '' &&
    phoneMiddle.length === 4 &&
    phoneLast.length === 4 &&
    enrollmentType !== '';

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fbc = params.get('fbc');
    const fbp = params.get('fbp');
    if (fbc && fbc.length > 0) {
      document.cookie = `_fbc=${fbc}; domain=.suneungsunbae.com; path=/; max-age=7776000; secure; samesite=lax`;
    }
    if (fbp && fbp.length > 0) {
      document.cookie = `_fbp=${fbp}; domain=.suneungsunbae.com; path=/; max-age=7776000; secure; samesite=lax`;
    }
  }, []);

  useEffect(() => {
    track('Waiting List Page Viewed', { page_type: 'briefing_center' });
  }, []);

  useEffect(() => {
    document.title = branch.pageTitle;
  }, [branch.pageTitle]);

  const onSelectBranch = (i: number) => {
    if (i === branchIdx) return;
    const next = BRIEFING_BRANCHES[i];
    setBranchIdx(i);
    try {
      window.history.replaceState(null, '', '#' + encodeURIComponent(next.introduceLocationKey));
    } catch {
      // no-op
    }
    track('Briefing Center Tab Switched', { location: next.location });
  };

  const submit = () => {
    if (ALL_BRIEFING_CLOSED) return;

    const telephoneNumber = '010' + phoneMiddle + phoneLast;

    setLoading(true);

    const data = {
      location: branch.location,
      select: gender,
      select2: enrollmentType,
      name,
      telephoneNumber,
      cert: '111111',
      etc: attendanceType,
      year: 2026,
      type1: 'winter26',
    };

    fetch('https://peetsunbae.com/waiting/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data }),
    })
      .then((res: any) => res.json())
      .then((result: any) => {
        setLoading(false);

        if (result.message === 'fail' || result.message === 'error') {
          track('Waiting List Submit Failed', {
            reason: result.message,
            location: branch.location,
            form_type: formType,
          });
          alert('오류 관리자 문의(010-9880-0489)');
          return;
        }

        if (result.message === 'success') {
          identifyUser(telephoneNumber, {
            $name: name,
            $phone: telephoneNumber,
            waiting_list_location: branch.location,
            student_type: enrollmentType,
            gender,
            attendance_type: attendanceType,
            waiting_list_date: new Date().toISOString(),
          });
          track('Waiting List Submitted', {
            location: branch.location,
            gender,
            attendance_type: attendanceType,
            student_type: enrollmentType,
            form_type: formType,
          });
          const eventId = `lead_briefing_${telephoneNumber}_${Date.now()}`;
          if ((window as any).fbq) {
            (window as any).fbq(
              'track',
              'Lead',
              {
                content_name: 'briefing_request',
                source: 'briefing',
                value: 100000,
                currency: 'KRW',
              },
              { eventID: eventId }
            );
          }
          sendLeadToCapi({
            eventId,
            phone: telephoneNumber,
            name,
            location: branch.location,
            contentName: 'briefing_request',
          });
          navigate('/registerwaitingfinish', { state: { id: result.id } });
        }
      });
  };

  return (
    <>
      <div className={styles.page}>
        <header className={styles.landingHeader}>
          <a href="#" className={styles.landingLogo} aria-label="수능선배">
            <Logo className={styles.landingLogoImg} />
          </a>
        </header>

        <nav className={tabStyles.branches} role="tablist" aria-label="설명회 지점 선택">
          <div className={tabStyles.branchesInner}>
            {BRIEFING_BRANCHES.map((b, i) => (
              <button
                key={b.location}
                type="button"
                role="tab"
                aria-selected={i === branchIdx}
                className={`${tabStyles.branchTab} ${i === branchIdx ? tabStyles.branchTabActive : ''}`}
                onClick={() => onSelectBranch(i)}
              >
                {b.branchName}
              </button>
            ))}
          </div>
        </nav>

        <div className={styles.eventHeader}>
          <h2 className={styles.eventEyebrow}>
            {branch.heroEyebrowMobile.split('\n').map((line, i, arr) => (
              <React.Fragment key={i}>
                {line}
                {i < arr.length - 1 ? <br /> : null}
              </React.Fragment>
            ))}
          </h2>
          <p className={styles.eventTagline}>{branch.heroTagline}</p>
          <hr className={styles.hair} />
        </div>

        <div className={styles.eventMeta}>
          <div className={styles.metaCol}>
            <span className={styles.metaKey}>일시</span>
            <span className={styles.metaVal}>{branch.eventDateMeta}</span>
            <span className={styles.metaSub}>신청자 문자 공지</span>
          </div>
          <div className={styles.metaCol}>
            <span className={styles.metaKey}>장소</span>
            <span className={styles.metaVal}>{branch.metaPlaceLine1Mobile}</span>
            <span className={styles.metaSub}>{branch.metaPlaceLine2Mobile}</span>
          </div>
          <div className={styles.metaCol}>
            <span className={styles.metaKey}>대상</span>
            <span className={styles.metaVal}>재학생 · N수생</span>
            <span className={styles.metaSub}>학부모 동반 가능</span>
          </div>
          <div className={styles.metaCol}>
            <span className={styles.metaKey}>정원</span>
            <span className={styles.metaVal}>선착순 진행</span>
            <span className={styles.metaSub}>마감 시 대기 등록</span>
          </div>
        </div>

        <MobileSocialProofBand key={branch.location} branch={branch} />

        <div className={styles.cntHeading}>
          <h2 className={styles.cntHeadingTitle}>
            {'참여자 전원에게\n드리는 한정 혜택'}
          </h2>
        </div>

        <div className={styles.benefitList}>
          <div className={styles.benefitRow}>
            <div className={styles.benefitText}>
              {'Medical 296 Databook\n8개 유형으로 분류한 합격 멘토 296인의 점수 · 진도 · 노하우'}
            </div>
            <div className={styles.benefitImage}>
              <video
                className={styles.benefitVideo}
                src="https://pub-81fe206b05a84c2da16285ce68449232.r2.dev/sunbae/blog/1/f_c_f_mp_.mp4"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
              />
              <div className={styles.sticker}>
                <div className={styles.stickerEn}>SUNBAE</div>
                <div className={styles.stickerSub}>독점 제공</div>
              </div>
            </div>
          </div>

          <div className={styles.benefitRow}>
            <div className={styles.benefitText}>{branch.benefitRegistrationText}</div>
            <div className={styles.benefitImage}>
              <img
                className={styles.benefitImg}
                src="https://pub-81fe206b05a84c2da16285ce68449232.r2.dev/sunbae/blog/1/pc2.webp"
                alt=""
              />
            </div>
          </div>

          <div className={styles.benefitRow}>
            <div className={styles.benefitText}>좌석 우선 지정권</div>
            <div className={styles.benefitImage}>
              <img
                className={styles.benefitImg}
                src="https://pub-81fe206b05a84c2da16285ce68449232.r2.dev/sunbae/blog/1/pc3.webp"
                alt=""
              />
            </div>
          </div>
        </div>

        <div className={styles.cntHeading}>
          <h2 className={styles.cntHeadingTitle}>설명회 예약하기</h2>
        </div>

        <div className={styles.reservationWrap}>
          <div className={styles.infoCard}>
            <p className={styles.infoCardTitle}>{branch.pageTitle}</p>
            <hr className={styles.hair} />
            <div className={styles.infoList}>
              <div className={styles.infoRow}>
                <p className={styles.infoLabel}>일시</p>
                <p className={styles.infoValue}>{branch.eventDateMobile}</p>
              </div>
              <div className={styles.infoRow}>
                <p className={styles.infoLabel}>대상</p>
                <p className={styles.infoValue}>재학생 / N수생 (학부모 동반 가능)</p>
              </div>
              <div className={styles.infoRow}>
                <p className={styles.infoLabel}>장소</p>
                <p className={styles.infoValue}>{branch.infoCardAddressMobile}</p>
              </div>
            </div>
            <hr className={styles.hair} />
            <p className={styles.infoDisclaimer}>
              예약은 선착순으로 진행됩니다.<br />
              등록 연락처로 안내 문자를 발송해 드립니다.
            </p>
          </div>

          <div className={styles.form}>
            <div className={styles.formFields}>
              <div className={styles.formField}>
                <p className={styles.formLabel}>참석 구분</p>
                <CustomSelect
                  value={attendanceType}
                  onChange={setAttendanceType}
                  disabled={ALL_BRIEFING_CLOSED}
                  placeholder="참석 구분을 선택해주세요"
                  options={[
                    { value: 'attend', label: '설명회 참석' },
                    { value: 'sms', label: '안내 문자 수신' },
                  ]}
                />
              </div>

              <div className={styles.formField}>
                <p className={styles.formLabel}>학생 성별</p>
                <CustomSelect
                  value={gender}
                  onChange={setGender}
                  disabled={ALL_BRIEFING_CLOSED}
                  placeholder="학생 성별을 선택해주세요"
                  options={[
                    { value: 'male', label: '남학생' },
                    { value: 'female', label: '여학생' },
                  ]}
                />
              </div>

              <div className={styles.formField}>
                <p className={styles.formLabel}>성명</p>
                <input
                  className={styles.textInput}
                  type="text"
                  placeholder="성명을 입력해 주세요"
                  value={name}
                  disabled={ALL_BRIEFING_CLOSED}
                  onChange={e => setName(e.target.value)}
                />
              </div>

              <div className={styles.formField}>
                <p className={styles.formLabel}>연락처</p>
                <div className={styles.phoneRow}>
                  <div className={styles.phonePrefix010}>
                    <span className={styles.selectValue}>010</span>
                    <ChevronDown />
                  </div>
                  <input
                    className={styles.phoneInput}
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    value={phoneMiddle}
                    disabled={ALL_BRIEFING_CLOSED}
                    onChange={e => {
                      const val = e.target.value.replace(/\D/g, '');
                      setPhoneMiddle(val);
                      if (val.length === 4) phoneLastRef.current?.focus();
                    }}
                  />
                  <input
                    ref={phoneLastRef}
                    className={styles.phoneInput}
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    value={phoneLast}
                    disabled={ALL_BRIEFING_CLOSED}
                    onChange={e => setPhoneLast(e.target.value.replace(/\D/g, ''))}
                  />
                </div>
              </div>

              <div className={styles.formField}>
                <p className={styles.formLabel}>재학 구분</p>
                <CustomSelect
                  value={enrollmentType}
                  onChange={setEnrollmentType}
                  disabled={ALL_BRIEFING_CLOSED}
                  placeholder="재학 구분을 선택해주세요"
                  options={[
                    { value: 'current', label: '재학생' },
                    { value: 'n', label: 'N수생' },
                  ]}
                />
              </div>
            </div>

            <button
              className={styles.submitBtn}
              type="button"
              disabled={ALL_BRIEFING_CLOSED || !isFormValid || loading}
              onClick={submit}
            >
              {ALL_BRIEFING_CLOSED
                ? <span className={styles.submitBtnText}>예약 마감</span>
                : loading
                  ? <CircularProgress color="inherit" size={22} />
                  : <span className={styles.submitBtnText}>예약하기</span>
              }
            </button>
          </div>
        </div>
      </div>

      <div className={styles.fixedDiv} style={{ display: keyboardOpen ? 'none' : 'block' }}>
        <div className={styles.fixedDivInner}>
          <div className={styles.fixedDivInner1}>
            <Logo className={styles.fixedDivLogoImg} />
          </div>
          <div className={styles.fixedDivInner2}>
            <button
              className={styles.fixedDivBtn1}
              onClick={() => window.open(
                `/introducedetail?location=${encodeURIComponent(branch.introduceLocationKey)}`,
                '_blank'
              )}
            >
              {branch.fixedBarIntroLabel}
            </button>
            {!isSoNarrow && (
              <button
                className={styles.fixedDivBtn2}
                onClick={() => { window.location.href = branch.telHref; }}
              >
                <PhoneSvg className={styles.fixedDivBtn2Img} />
              </button>
            )}
            <button
              className={styles.fixedDivBtn2}
              onClick={() => window.open(branch.kakaoUrl, '_blank')}
            >
              <SpeechBubble className={styles.fixedDivBtn2Img} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
